import { BRAND, SITE_ORIGIN } from "../brand";
import { marketingLinks, MarketingShell, socialImageMeta } from "../components/marketing/layout";

export const links = marketingLinks;

export const meta = () => [
  { title: `Privacy Policy for ${BRAND.name} — Shopify` },
  { name: "description", content: `Privacy policy for ${BRAND.name}, a B2B quote/PO/AI counter-offer Shopify app from Legitimate LLC.` },
  { name: "robots", content: "index,follow" },
  { property: "og:type", content: "website" },
  { property: "og:url", content: `${SITE_ORIGIN}/privacy` },
  { tagName: "link", rel: "canonical", href: `${SITE_ORIGIN}/privacy` },
  ...socialImageMeta(),
];

export default function Privacy() {
  return (
    <MarketingShell>
      <section className="qd-section qd-legal">
        <p className="qd-eyebrow">Legal</p>
        <h1 style={{ marginTop: "0.8rem" }}>Privacy Policy — {BRAND.name}</h1>
        <p className="qd-legal__lede">Last updated: 2026-05-19</p>

        <h2>Who we are</h2>
        <p>
          {BRAND.name} ("we", "our", "us") is a Shopify app published by Legitimate LLC. We provide quote / RFQ workflow,
          negotiated per-quote pricing, PO PDF parsing, and AI counter-offer drafting for Shopify merchants. This Privacy
          Policy describes what data we collect, how we use it, and the rights you have over it.
        </p>

        <h2>Data we collect from merchants who install our app</h2>
        <ul>
          <li><strong>Shop identity</strong>: your <code>myshopify.com</code> domain, store name, and primary contact email.</li>
          <li><strong>Shopify session tokens</strong>: encrypted access tokens used to call the Shopify Admin API on your behalf.</li>
          <li><strong>B2B catalog data</strong>: company names, locations, contacts, catalog price data, and catalog references.</li>
          <li><strong>Order history</strong>: when you enable the AI counter-offer engine, we read your historical orders (line items, quantities, prices, dates) for the company being quoted. We do not store the full order; we store a compressed corpus (per-variant aggregates).</li>
          <li><strong>Quotes you create</strong>: line items, custom prices, expiry, notes, buyer email and name.</li>
          <li><strong>PO PDFs you upload</strong>: stored temporarily for parsing. Deleted within 30 days of parse completion unless you re-process them.</li>
        </ul>

        <h2>Data we collect from your B2B buyers</h2>
        <ul>
          <li>Buyer email and name as you enter them on a quote.</li>
          <li>Open / accept / decline timestamps on quotes the buyer interacts with.</li>
          <li>Buyer-side notes if entered on the buyer portal.</li>
        </ul>
        <p>We do not collect IP addresses, browser fingerprints, or any other passive tracking data.</p>

        <h2>How we use this data</h2>
        <ul>
          <li>Operate the quote workflow you installed us for.</li>
          <li>Generate AI counter-offer suggestions when you trigger them. The compressed corpus is sent to our LLM provider (OpenRouter) and not retained by them beyond the inference call.</li>
          <li>Parse uploaded PO PDFs via our LLM provider (OpenRouter).</li>
          <li>Bill you via Shopify Billing — we never see your payment card information; Shopify handles that entirely.</li>
          <li>Email transactional notifications about quotes (sent / accepted / declined) via our email provider (Resend) when you've enabled that integration.</li>
        </ul>

        <h2>Subprocessors</h2>
        <p>We share data with the following processors strictly to operate the service:</p>
        <ul>
          <li><strong>Shopify</strong> — platform host, billing, OAuth.</li>
          <li><strong>Railway</strong> — application hosting and managed Postgres database.</li>
          <li><strong>Trigger.dev</strong> — durable background job execution (PO parse, counter-offer drafting).</li>
          <li><strong>OpenRouter</strong> — LLM gateway for PO parsing and counter-offer generation.</li>
          <li><strong>Resend</strong> (optional) — transactional email when enabled.</li>
          <li><strong>Sentry</strong> (optional) — error monitoring when enabled. Sentry receives stack traces but not user data.</li>
        </ul>

        <h2>Data retention</h2>
        <p>
          We retain your data for the lifetime of your install plus a 30-day reinstall window. After 30 days of being uninstalled,
          or upon receipt of a shop redact webhook from Shopify, we hard-delete every record we hold for your shop, including
          quotes, PO imports, pricing rules, and counter-offer suggestions.
        </p>
        <p>
          On receipt of a customer redact webhook from Shopify, we anonymize the buyer email and name on every quote referencing
          that customer within 30 days.
        </p>

        <h2>GDPR / CCPA rights</h2>
        <p>
          EU and California residents have the right to access, correct, port, or delete personal data we hold about you. Email
          <a href={`mailto:${BRAND.supportEmail}`}> {BRAND.supportEmail}</a> with the request and proof of identity; we will fulfill
          within 30 days. You may also exercise these rights through your Shopify merchant via Shopify's data request and redact
          flows, which we honor automatically.
        </p>

        <h2>Security</h2>
        <p>
          All data is transmitted over TLS. Database storage is encrypted at rest by our hosting provider. Shopify session tokens
          are stored in our database in encrypted form. We do not store payment card numbers — billing is handled entirely by
          Shopify Billing. HMAC verification is enforced on every webhook we receive from Shopify; tampered payloads are rejected.
        </p>

        <h2>Children's privacy</h2>
        <p>{BRAND.name} is a B2B sales tool and is not directed at children under 13.</p>

        <h2>Changes to this policy</h2>
        <p>
          We may update this policy from time to time. Material changes will be communicated via email to the support address on
          record for your install, and the "Last updated" date above will change.
        </p>

        <h2>Contact</h2>
        <p>
          Questions: <a href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a><br />
          Legitimate LLC. Mailing address available on request.
        </p>
      </section>
    </MarketingShell>
  );
}
